import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Shield, Database, Mail, Globe, Key, ChevronRight, ChevronLeft,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, Plug, Zap,
  Eye, EyeOff, ArrowRight, Loader2, PartyPopper, Lock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const WIZARD_STEPS = [
  { id: 'overview', title: 'Overview', icon: Zap, description: 'Integration overview' },
  { id: 'scanners', title: 'Scanners', icon: Shield, description: 'Vulnerability scanners' },
  { id: 'threat-intel', title: 'Threat Intel', icon: Database, description: 'Threat feeds' },
  { id: 'notifications', title: 'Notifications', icon: Mail, description: 'Alerts & emails' },
  { id: 'review', title: 'Review', icon: PartyPopper, description: 'Summary' },
];

const INTEGRATION_DEFS = {
  nessus: {
    name: 'Tenable Nessus',
    category: 'scanners',
    description: 'Enterprise vulnerability scanner for network and web application security.',
    fields: [
      { key: 'url', label: 'Nessus Server URL', placeholder: 'https://nessus.example.com:8834', type: 'text' },
      { key: 'access_key', label: 'Access Key', placeholder: 'Your Nessus access key', type: 'password' },
      { key: 'secret_key', label: 'Secret Key', placeholder: 'Your Nessus secret key', type: 'password' },
    ],
    docsUrl: 'https://docs.tenable.com/nessus/Content/GenerateAnAPIKey.htm',
  },
  qualys: {
    name: 'Qualys Cloud',
    category: 'scanners',
    description: 'Cloud-based vulnerability management and compliance platform.',
    fields: [
      { key: 'url', label: 'Qualys API URL', placeholder: 'https://qualysapi.qualys.com', type: 'text' },
      { key: 'username', label: 'Username', placeholder: 'API username', type: 'text' },
      { key: 'password', label: 'Password', placeholder: 'API password', type: 'password' },
    ],
    docsUrl: 'https://www.qualys.com/docs/qualys-api-vmpc-user-guide.htm',
  },
  openvas: {
    name: 'OpenVAS / Greenbone',
    category: 'scanners',
    description: 'Open-source vulnerability scanner for comprehensive security assessments.',
    fields: [
      { key: 'host', label: 'OpenVAS Host', placeholder: 'openvas.example.com', type: 'text' },
      { key: 'username', label: 'Username', placeholder: 'admin', type: 'text' },
      { key: 'password', label: 'Password', placeholder: 'Password', type: 'password' },
    ],
    docsUrl: 'https://greenbone.github.io/docs/latest/',
  },
  alienvault_otx: {
    name: 'AlienVault OTX',
    category: 'threat-intel',
    description: 'Open Threat Exchange for community-driven threat intelligence.',
    fields: [
      { key: 'api_key', label: 'OTX API Key', placeholder: 'Your OTX API key', type: 'password' },
    ],
    docsUrl: 'https://otx.alienvault.com/api',
  },
  nvd: {
    name: 'NVD API Key (Optional)',
    category: 'threat-intel',
    description: 'NIST NVD works without a key. Adding one increases your rate limit from 5 to 50 requests/30s.',
    fields: [
      { key: 'api_key', label: 'NVD API Key', placeholder: 'Optional - enhances rate limits', type: 'password' },
    ],
    docsUrl: 'https://nvd.nist.gov/developers/request-an-api-key',
    optional: true,
  },
  email_resend: {
    name: 'Resend Email',
    category: 'notifications',
    description: 'Transactional email for SLA warnings, compliance alerts, and weekly digests.',
    fields: [
      { key: 'api_key', label: 'Resend API Key', placeholder: 're_xxxxxxxxxxxx', type: 'password' },
    ],
    docsUrl: 'https://resend.com/docs/api-reference/api-keys/create-api-key',
  },
  slack: {
    name: 'Slack Notifications',
    category: 'notifications',
    description: 'Real-time compliance alerts delivered to your Slack channel.',
    fields: [
      { key: 'webhook_url', label: 'Webhook URL', placeholder: 'https://hooks.slack.com/services/...', type: 'text' },
    ],
    docsUrl: 'https://api.slack.com/messaging/webhooks',
  },
  teams: {
    name: 'Microsoft Teams',
    category: 'notifications',
    description: 'Real-time compliance alerts delivered to your Teams channel.',
    fields: [
      { key: 'webhook_url', label: 'Webhook URL', placeholder: 'https://outlook.office.com/webhook/...', type: 'text' },
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook',
  },
};

function CredentialInput({ field, value, onChange }) {
  const [visible, setVisible] = useState(false);
  const isSecret = field.type === 'password';

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-700">{field.label}</label>
      <div className="relative">
        <Input
          type={isSecret && !visible ? 'password' : 'text'}
          placeholder={field.placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          className="pr-10 font-mono text-sm"
          data-testid={`input-${field.key}`}
        />
        {isSecret && (
          <button
            type="button"
            onClick={() => setVisible(!visible)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            {visible ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
    </div>
  );
}

function IntegrationCard({ id, def, credentials, onChange, onTest, testResult, testing }) {
  const allFieldsFilled = def.fields.every(f => credentials[f.key]?.trim());

  return (
    <Card className="border-slate-200 hover:border-slate-300 transition-colors" data-testid={`wizard-card-${id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">{def.name}</CardTitle>
            <CardDescription className="text-sm mt-0.5">{def.description}</CardDescription>
          </div>
          {testResult && (
            <Badge className={testResult.connected
              ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
              : 'bg-red-100 text-red-800 border-red-200'
            }>
              {testResult.connected ? 'Connected' : 'Failed'}
            </Badge>
          )}
          {def.optional && !testResult && (
            <Badge variant="outline" className="text-slate-500">Optional</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {def.fields.map((field) => (
          <CredentialInput
            key={field.key}
            field={field}
            value={credentials[field.key]}
            onChange={onChange}
          />
        ))}

        <div className="flex items-center justify-between pt-2">
          <a
            href={def.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <Key size={12} /> Where to get credentials
          </a>

          <Button
            variant="outline"
            size="sm"
            disabled={!allFieldsFilled || testing}
            onClick={() => onTest(id, credentials)}
            data-testid={`test-btn-${id}`}
          >
            {testing ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Zap className="mr-1.5 h-3.5 w-3.5" />
            )}
            Test Connection
          </Button>
        </div>

        {testResult && (
          <div className={`text-sm p-3 rounded-md ${
            testResult.connected
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {testResult.connected ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} />
                {testResult.message}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <XCircle size={16} />
                {testResult.message}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function IntegrationWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('grc_token');
      const res = await axios.get(`${API}/api/integrations/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationStatus(res.data);
    } catch (err) {
      toast.error('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialChange = (integrationId, field, value) => {
    setCredentials(prev => ({
      ...prev,
      [integrationId]: { ...(prev[integrationId] || {}), [field]: value }
    }));
    // Clear test result when credentials change
    setTestResults(prev => {
      const next = { ...prev };
      delete next[integrationId];
      return next;
    });
  };

  const handleTestConnection = async (integrationId, creds) => {
    setTesting(integrationId);
    try {
      const token = localStorage.getItem('grc_token');
      const res = await axios.post(
        `${API}/api/integrations/${integrationId}/test-connection`,
        { credentials: creds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResults(prev => ({ ...prev, [integrationId]: res.data }));
      if (res.data.connected) {
        toast.success(`${INTEGRATION_DEFS[integrationId].name}: Connected!`);
      } else {
        toast.error(`${INTEGRATION_DEFS[integrationId].name}: ${res.data.message}`);
      }
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [integrationId]: { connected: false, message: 'Connection test failed' }
      }));
      toast.error('Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    const token = localStorage.getItem('grc_token');
    let savedCount = 0;

    for (const [integrationId, creds] of Object.entries(credentials)) {
      const hasValues = Object.values(creds).some(v => v?.trim());
      if (!hasValues) continue;

      try {
        await axios.post(
          `${API}/api/integrations/configure`,
          { integration_id: integrationId, credentials: creds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        savedCount++;
      } catch (err) {
        toast.error(`Failed to save ${INTEGRATION_DEFS[integrationId]?.name}`);
      }
    }

    if (savedCount > 0) {
      toast.success(`Saved ${savedCount} integration(s) successfully`);
      await fetchStatus();
    }
    setSaving(false);
  };

  const getIntegrationsForStep = (stepId) => {
    return Object.entries(INTEGRATION_DEFS).filter(([, def]) => def.category === stepId);
  };

  const step = WIZARD_STEPS[currentStep];
  const progress = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  const configuredCount = integrationStatus?.summary?.active || 0;
  const totalCount = integrationStatus?.summary?.total || 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-8" data-testid="integration-wizard">
        {/* Progress Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Integration Setup Wizard</h1>
              <p className="text-sm text-slate-500 mt-1">
                Step {currentStep + 1} of {WIZARD_STEPS.length}: {step.description}
              </p>
            </div>
            <Badge variant="outline" className="text-sm">
              {configuredCount}/{totalCount} Active
            </Badge>
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        {/* Step Navigation Pills */}
        <div className="flex gap-1 mb-8 p-1 bg-slate-100 rounded-lg overflow-x-auto">
          {WIZARD_STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === currentStep;
            const isPast = i < currentStep;
            return (
              <button
                key={s.id}
                onClick={() => setCurrentStep(i)}
                data-testid={`wizard-step-${s.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all whitespace-nowrap flex-1 justify-center ${
                  isActive
                    ? 'bg-white text-slate-900 shadow-sm'
                    : isPast
                    ? 'text-emerald-700 hover:bg-white/50'
                    : 'text-slate-500 hover:bg-white/50'
                }`}
              >
                {isPast ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Icon size={16} />}
                <span className="hidden sm:inline">{s.title}</span>
              </button>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="space-y-6 mb-8">
          {/* Overview Step */}
          {step.id === 'overview' && (
            <div className="space-y-6">
              <Card className="border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Plug size={24} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold mb-2">Connect Your Security Tools</h2>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        This wizard walks you through configuring each integration step by step.
                        You can test each connection before saving, and skip any integrations you
                        don't need right now. All credentials are stored securely.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Status Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { label: 'Vulnerability Scanners', count: 3, icon: Shield,
                    active: [integrationStatus?.integrations?.scanners?.nessus,
                             integrationStatus?.integrations?.scanners?.qualys,
                             integrationStatus?.integrations?.scanners?.openvas].filter(s => s?.configured).length },
                  { label: 'Threat Intelligence', count: 3, icon: Database,
                    active: [integrationStatus?.integrations?.threat_intelligence?.nvd,
                             integrationStatus?.integrations?.threat_intelligence?.mitre_attack,
                             integrationStatus?.integrations?.threat_intelligence?.alienvault_otx].filter(s => s?.configured).length },
                  { label: 'Notifications', count: 3, icon: Mail,
                    active: [integrationStatus?.integrations?.notifications?.email_resend,
                             integrationStatus?.integrations?.notifications?.slack,
                             integrationStatus?.integrations?.notifications?.teams].filter(s => s?.configured).length },
                ].map((category) => {
                  const Icon = category.icon;
                  return (
                    <Card key={category.label} className="border-slate-200">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Icon size={18} className="text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{category.label}</p>
                            <p className="text-xs text-slate-500">{category.active}/{category.count} configured</p>
                          </div>
                        </div>
                        <Progress value={(category.active / category.count) * 100} className="h-1" />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 flex items-start gap-3">
                <Lock size={18} className="text-slate-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-700 mb-1">Security Note</p>
                  <p>Credentials are encrypted and stored in your organization's database. Only admins can view or modify integration settings. You can update or remove credentials at any time.</p>
                </div>
              </div>
            </div>
          )}

          {/* Scanner / Threat Intel / Notification Steps */}
          {['scanners', 'threat-intel', 'notifications'].includes(step.id) && (
            <div className="space-y-5">
              {getIntegrationsForStep(step.id).map(([id, def]) => (
                <IntegrationCard
                  key={id}
                  id={id}
                  def={def}
                  credentials={credentials[id] || {}}
                  onChange={(field, value) => handleCredentialChange(id, field, value)}
                  onTest={handleTestConnection}
                  testResult={testResults[id]}
                  testing={testing === id}
                />
              ))}
            </div>
          )}

          {/* Review Step */}
          {step.id === 'review' && (
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Configuration Summary</CardTitle>
                  <CardDescription>Review your integration settings before saving.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="divide-y divide-slate-100">
                    {Object.entries(INTEGRATION_DEFS).map(([id, def]) => {
                      const creds = credentials[id] || {};
                      const hasCredentials = Object.values(creds).some(v => v?.trim());
                      const result = testResults[id];

                      return (
                        <div key={id} className="flex items-center justify-between py-3" data-testid={`review-${id}`}>
                          <div className="flex items-center gap-3">
                            {result?.connected ? (
                              <CheckCircle2 size={18} className="text-emerald-500" />
                            ) : hasCredentials ? (
                              <AlertTriangle size={18} className="text-amber-500" />
                            ) : (
                              <div className="w-[18px] h-[18px] rounded-full border-2 border-slate-300" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-slate-900">{def.name}</p>
                              <p className="text-xs text-slate-500">
                                {result?.connected
                                  ? 'Connected & tested'
                                  : hasCredentials
                                  ? 'Credentials entered (not tested)'
                                  : 'Not configured'}
                              </p>
                            </div>
                          </div>
                          <Badge className={
                            result?.connected
                              ? 'bg-emerald-100 text-emerald-800'
                              : hasCredentials
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-slate-100 text-slate-600'
                          }>
                            {result?.connected ? 'Ready' : hasCredentials ? 'Untested' : 'Skipped'}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Button
                className="w-full h-12 text-base"
                onClick={handleSaveAll}
                disabled={saving}
                data-testid="save-all-btn"
              >
                {saving ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 h-5 w-5" />
                )}
                Save All Configurations
              </Button>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between border-t border-slate-200 pt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            data-testid="wizard-prev-btn"
          >
            <ChevronLeft className="mr-1.5 h-4 w-4" /> Back
          </Button>

          <div className="flex items-center gap-3">
            {currentStep < WIZARD_STEPS.length - 1 ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/integrations')}
                  className="text-slate-500"
                  data-testid="wizard-exit-btn"
                >
                  Exit Wizard
                </Button>
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  data-testid="wizard-next-btn"
                >
                  Next <ChevronRight className="ml-1.5 h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button
                onClick={() => { handleSaveAll().then(() => navigate('/integrations')); }}
                disabled={saving}
                data-testid="wizard-finish-btn"
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Finish & Save <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
